// #region Imports
import { createSelector, createSlice, SerializedError } from "@reduxjs/toolkit";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { marketplaceTeamsApi } from "marketplace/teams/_api/marketplace-team-api";
import { TeamJSON } from "marketplace/teams/_schema/team-schema";
import { TeamInvitationJSON } from "marketplace/teams/invitations/_schemas/team-invitation/team-invitation-schema";
import TeamInvitation from "marketplace/teams/invitations/team-invitation";
import Team from "marketplace/teams/team";
// #endregion

// #region Store State Type
export type MarketplaceTeamStoreState = {
  create: {
    saving: boolean;
    error: SerializedError | null;
  };
  vendors: {
    entries?: TeamJSON[];
    loading: boolean;
    error: SerializedError | null;
  };
  byId: {
    entries: Record<string, TeamJSON>;
    loading: Record<string, boolean>;
    saving: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
  avatars: {
    create: {
      saving: boolean;
      error: SerializedError | null;
    };
    entries: Record<string, string>;
    loading: Record<string, boolean>;
    saving: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
  invitations: {
    create: {
      saving: boolean;
      error: SerializedError | null;
    };
    byTeamId: {
      entries: Record<string, TeamInvitationJSON[]>;
      loading: Record<string, boolean>;
      saving: Record<string, boolean>;
      error: Record<string, SerializedError | null>;
    };
    byId: {
      entries: Record<string, TeamInvitationJSON>;
      loading: Record<string, boolean>;
      saving: Record<string, boolean>;
      error: Record<string, SerializedError | null>;
    };
  };
};
// #region Initial State
const initialState: MarketplaceTeamStoreState = {
  create: {
    saving: false,
    error: null,
  },
  vendors: {
    entries: undefined,
    loading: false,
    error: null,
  },
  byId: {
    entries: {},
    loading: {},
    saving: {},
    error: {},
  },
  avatars: {
    create: {
      saving: false,
      error: null,
    },
    entries: {},
    loading: {},
    saving: {},
    error: {},
  },
  invitations: {
    create: {
      saving: false,
      error: null,
    },
    byTeamId: {
      entries: {},
      loading: {},
      saving: {},
      error: {},
    },
    byId: {
      entries: {},
      loading: {},
      saving: {},
      error: {},
    },
  },
};

// #region Slice
export const marketplaceTeamsSlice = createSlice({
  name: "marketplaceTeams",
  initialState,
  // #region Simple Reducers
  reducers: {
    addMarketplaceTeam: (state, action) => {
      const teamId = action.payload.id?.value;
      if (!teamId) return;
      state.byId.entries[teamId] = action.payload;
      state.vendors.entries = state.vendors.entries?.filter((team) => team.id !== teamId) ?? [];
      state.vendors.entries.push(action.payload);
    },
    addMarketplaceTeamJSON: (state, action: { payload: TeamJSON }) => {
      const teamJSON = action.payload;
      if (!teamJSON.id) return;

      state.byId.entries[teamJSON.id] = teamJSON;
      state.vendors.entries = state.vendors.entries?.filter((team) => team.id !== teamJSON.id) ?? [];
      state.vendors.entries.push(teamJSON);
    },
    removeMarketplaceTeam: (state, action) => {
      delete state.byId.entries[action.payload.value];
    },
    addAvatar: (state, action) => {
      state.avatars.entries[action.payload.teamId.value] = action.payload.avatar;
    },
    removeAvatar: (state, action) => {
      delete state.avatars.entries[action.payload.value];
    },
    addMarketplaceTeamInvitation: (state, action) => {
      const invitationId = action.payload.id?.value;
      if (!invitationId) return;
      state.invitations.byId.entries[invitationId] = action.payload;
    },
    removeMarketplaceTeamInvitation: (state, action) => {
      const invitationId = action.payload.value;
      if (!invitationId) return;
      delete state.invitations.byId.entries[invitationId];
      for (const teamId in state.invitations.byTeamId.entries) {
        state.invitations.byTeamId.entries[teamId] = state.invitations.byTeamId.entries[teamId].filter(
          (invitation) => invitation.id !== invitationId,
        );
      }
    },
  },
  // #region Async Reducers
  extraReducers: (builder) => {
    // #region getTeamVendors
    builder.addMatcher(marketplaceTeamsApi.endpoints.getTeamVendors.matchPending, (state) => {
      state.vendors.loading = true;
      state.vendors.error = null;
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.getTeamVendors.matchFulfilled, (state, action) => {
      state.vendors.entries = action.payload;
      state.vendors.loading = false;
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.getTeamVendors.matchRejected, (state, action) => {
      state.vendors.loading = false;
      state.vendors.error = action.error;
    });
    // #endregion

    // #region getTeamById
    builder.addMatcher(marketplaceTeamsApi.endpoints.getTeamById.matchPending, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;
      state.byId.loading[teamId] = true;
      state.byId.error[teamId] = null;
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.getTeamById.matchFulfilled, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;
      state.byId.loading[teamId] = false;
      if (!action.payload) {
        console.error("MarketplaceTeam not received by store.");
        return;
      }
      state.byId.entries[teamId] = action.payload;
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.getTeamById.matchRejected, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;
      state.byId.loading[teamId] = false;
      state.byId.error[teamId] = action.error;
    });
    // #endregion

    // #region getTeamsByIds
    builder.addMatcher(marketplaceTeamsApi.endpoints.getTeamsByIds.matchPending, (state, action) => {
      const ids = action.meta.arg.originalArgs.ids;
      for (const id of ids) {
        state.byId.loading[id] = true;
        state.byId.error[id] = null;
      }
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.getTeamsByIds.matchFulfilled, (state, action) => {
      const ids = action.meta.arg.originalArgs.ids;
      for (const team of action.payload) {
        state.byId.entries[team.id] = team;
      }
      for (const id of ids) {
        state.byId.loading[id] = false;
      }
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.getTeamsByIds.matchRejected, (state, action) => {
      const ids = action.meta.arg.originalArgs.ids;
      for (const id of ids) {
        state.byId.loading[id] = false;
        state.byId.error[id] = action.error;
      }
    });
    // #endregion

    // #region getTeamAvatar
    builder.addMatcher(marketplaceTeamsApi.endpoints.getTeamAvatar.matchPending, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;
      state.avatars.loading[teamId] = true;
      state.avatars.error[teamId] = null;
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.getTeamAvatar.matchFulfilled, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;
      state.avatars.entries[teamId] = action.payload;
      state.avatars.loading[teamId] = false;
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.getTeamAvatar.matchRejected, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;
      state.avatars.loading[teamId] = false;
      state.avatars.error[teamId] = action.error;
    });
    // #endregion

    // #region getTeamInvitationByInvitationId
    builder.addMatcher(marketplaceTeamsApi.endpoints.getTeamInvitationByInvitationId.matchPending, (state, action) => {
      const invitationId = action.meta.arg.originalArgs.invitationId;
      state.invitations.byId.loading[invitationId] = true;
      state.invitations.byId.error[invitationId] = null;
    });
    builder.addMatcher(
      marketplaceTeamsApi.endpoints.getTeamInvitationByInvitationId.matchFulfilled,
      (state, action) => {
        const invitationId = action.meta.arg.originalArgs.invitationId;
        state.invitations.byId.entries[invitationId] = action.payload;
        state.invitations.byId.loading[invitationId] = false;
      },
    );
    builder.addMatcher(marketplaceTeamsApi.endpoints.getTeamInvitationByInvitationId.matchRejected, (state, action) => {
      const invitationId = action.meta.arg.originalArgs.invitationId;
      state.invitations.byId.loading[invitationId] = false;
      state.invitations.byId.error[invitationId] = action.error;
    });
    // #endregion

    // #region getTeamInvitationsByTeamId
    builder.addMatcher(marketplaceTeamsApi.endpoints.getTeamInvitationsByTeamId.matchPending, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;
      state.invitations.byTeamId.loading[teamId] = true;
      state.invitations.byTeamId.error[teamId] = null;
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.getTeamInvitationsByTeamId.matchFulfilled, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;
      state.invitations.byTeamId.entries[teamId] = action.payload;
      state.invitations.byTeamId.loading[teamId] = false;
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.getTeamInvitationsByTeamId.matchRejected, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;
      state.invitations.byTeamId.loading[teamId] = false;
      state.invitations.byTeamId.error[teamId] = action.error;
    });
    // #endregion

    // #region createTeam
    builder.addMatcher(marketplaceTeamsApi.endpoints.createTeam.matchPending, (state) => {
      state.create.error = null;
      state.create.saving = true;
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.createTeam.matchFulfilled, (state, action) => {
      const teamId = action.payload.id;

      if (!teamId) return;

      state.create.error = null;
      state.create.saving = false;

      state.byId.entries[teamId] = action.payload;
      if (!state.vendors.entries) {
        state.vendors.entries = [];
      }
      state.vendors.entries.push(action.payload);
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.createTeam.matchRejected, (state, action) => {
      state.create.error = action.error;
      state.create.saving = false;
    });
    // #endregion

    // #region updateTeam
    builder.addMatcher(marketplaceTeamsApi.endpoints.updateTeam.matchPending, (state, action) => {
      const updatedTeamJson = action.meta.arg.originalArgs.updatedTeam.toJSON();
      const teamId = updatedTeamJson.id;

      if (!teamId) return;

      state.byId.entries[teamId] = updatedTeamJson;
      state.byId.error[teamId] = null;
      state.byId.saving[teamId] = true;

      const vendorIndex = state.vendors.entries?.findIndex((team) => team.id === teamId) ?? -1;
      if (state.vendors.entries && vendorIndex !== -1) {
        state.vendors.entries[vendorIndex] = updatedTeamJson;
      } else {
        state.vendors.entries = state.vendors.entries || [];
        state.vendors.entries.push(updatedTeamJson);
      }
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.updateTeam.matchFulfilled, (state, action) => {
      const teamId = action.payload.id;

      if (!teamId) return;

      state.byId.entries[teamId] = action.payload;
      state.byId.error[teamId] = null;
      state.byId.saving[teamId] = false;

      const vendorIndex = state.vendors.entries?.findIndex((team) => team.id === teamId) ?? -1;
      if (state.vendors.entries && vendorIndex !== -1) {
        state.vendors.entries[vendorIndex] = action.payload;
      } else {
        state.vendors.entries = state.vendors.entries || [];
        state.vendors.entries.push(action.payload);
      }
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.updateTeam.matchRejected, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;

      state.byId.error[teamId] = action.error;
      state.byId.saving[teamId] = false;
    });
    // #endregion

    // #region deleteTeam
    builder.addMatcher(marketplaceTeamsApi.endpoints.deleteTeam.matchPending, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;

      state.byId.error[teamId] = null;
      state.byId.saving[teamId] = true;
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.deleteTeam.matchFulfilled, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;

      delete state.byId.entries[teamId];
      state.byId.error[teamId] = null;
      state.byId.saving[teamId] = false;

      const targetIndex = state.vendors.entries?.findIndex((team) => team.id === teamId) ?? -1;
      if (targetIndex !== -1 && state.vendors.entries) {
        state.vendors.entries.splice(targetIndex, 1);
      }
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.deleteTeam.matchRejected, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;

      state.byId.error[teamId] = action.error;
      state.byId.saving[teamId] = false;
    });
    // #endregion

    // #region removeTeamMember
    builder.addMatcher(marketplaceTeamsApi.endpoints.removeTeamMember.matchPending, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;

      state.byId.error[teamId] = null;
      state.byId.saving[teamId] = true;
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.removeTeamMember.matchFulfilled, (state, action) => {
      const { teamId, memberIndividualId } = action.meta.arg.originalArgs;
      const team = state.byId.entries[teamId];

      if (team) {
        team.members = team.members.filter((member) => member.id !== memberIndividualId);
        state.byId.entries[teamId] = team;
      }

      state.byId.error[teamId] = null;
      state.byId.saving[teamId] = false;

      const vendorIndex = state.vendors.entries?.findIndex((t) => t.id === teamId) ?? -1;
      if (state.vendors.entries && vendorIndex !== -1 && team) {
        state.vendors.entries[vendorIndex] = team;
      }
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.removeTeamMember.matchRejected, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;

      state.byId.error[teamId] = action.error;
      state.byId.saving[teamId] = false;
    });
    // #endregion

    // #region createTeamAvatar
    builder.addMatcher(marketplaceTeamsApi.endpoints.createTeamAvatar.matchPending, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;

      state.avatars.error[teamId] = null;
      state.avatars.saving[teamId] = true;
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.createTeamAvatar.matchFulfilled, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;

      if (action.payload) {
        state.avatars.entries[teamId] = action.payload;
      }
      state.avatars.error[teamId] = null;
      state.avatars.saving[teamId] = false;
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.createTeamAvatar.matchRejected, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;

      state.avatars.error[teamId] = action.error;
      state.avatars.saving[teamId] = false;
    });
    // #endregion

    // #region createTeamInvitation
    builder.addMatcher(marketplaceTeamsApi.endpoints.createTeamInvitation.matchPending, (state) => {
      state.invitations.create.error = null;
      state.invitations.create.saving = true;
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.createTeamInvitation.matchFulfilled, (state, action) => {
      const teamId = action.payload.teamId;

      if (!state.invitations.byTeamId.entries[teamId]) {
        state.invitations.byTeamId.entries[teamId] = [];
      }

      state.invitations.byId.entries[action.payload.id] = action.payload;
      state.invitations.byTeamId.entries[teamId].push(action.payload);

      state.invitations.create.error = null;
      state.invitations.create.saving = false;
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.createTeamInvitation.matchRejected, (state, action) => {
      state.invitations.create.error = action.error;
      state.invitations.create.saving = false;
    });
    // #endregion

    // #region cancelTeamInvitation
    builder.addMatcher(marketplaceTeamsApi.endpoints.cancelTeamInvitation.matchPending, (state, action) => {
      const invitationId = action.meta.arg.originalArgs.invitationId;

      state.invitations.byId.error[invitationId] = null;
      state.invitations.byId.saving[invitationId] = true;
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.cancelTeamInvitation.matchFulfilled, (state, action) => {
      const invitationId = action.meta.arg.originalArgs.invitationId;

      for (const teamId in state.invitations.byTeamId.entries) {
        state.invitations.byTeamId.entries[teamId] = state.invitations.byTeamId.entries[teamId].filter(
          (invitation) => invitation.id !== invitationId,
        );
      }
      state.invitations.byTeamId.error[invitationId] = null;
      state.invitations.byTeamId.saving[invitationId] = false;

      for (const id in state.invitations.byId.entries) {
        if (id === invitationId) {
          delete state.invitations.byId.entries[id];
        }
      }
      state.invitations.byId.error[invitationId] = null;
      state.invitations.byId.saving[invitationId] = false;
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.cancelTeamInvitation.matchRejected, (state, action) => {
      const invitationId = action.meta.arg.originalArgs.invitationId;

      state.invitations.byId.error[invitationId] = action.error;
      state.invitations.byId.saving[invitationId] = false;
      state.invitations.byTeamId.error[invitationId] = action.error;
      state.invitations.byTeamId.saving[invitationId] = false;
    });
    // #endregion

    // #region promoteTeamMemberToLeader
    builder.addMatcher(marketplaceTeamsApi.endpoints.promoteTeamMemberToLeader.matchPending, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;

      state.byId.error[teamId] = null;
      state.byId.saving[teamId] = true;
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.promoteTeamMemberToLeader.matchFulfilled, (state, action) => {
      // Team data is invalidated; re-fetch via getTeamById will update state
      const teamId = action.meta.arg.originalArgs.teamId;

      state.byId.error[teamId] = null;
      state.byId.saving[teamId] = false;
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.promoteTeamMemberToLeader.matchRejected, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;

      state.byId.error[teamId] = action.error;
      state.byId.saving[teamId] = false;
    });
    // #endregion
  },
});

// #region Exports

// #region Team By Id

// getTeamById ----------------------------------------------------------------
const selectTeamById = createSelector(
  [
    (state: RootState) => state.teams.byId.entries,
    (_: RootState, id?: Guid) => id,
  ],
  (entries, id) => {
    if (!id) return undefined;
    const teamJson = entries[id.value];
    if (!teamJson) return undefined;
    return Team.fromJSON(teamJson);
  },
);
export const getTeamById = (id?: Guid) => (state: RootState) => selectTeamById(state, id);
// ----------------------------------------------------------------------------

export const getIsLoadingTeamById = (id?: Guid | null) => (state: RootState) => {
  if (!id) return false;
  return state.teams.byId.loading[id.value];
};

export const getErrorLoadingTeamById = (id?: Guid | null) => (state: RootState) => {
  if (!id) return;
  return state.teams.byId.error[id.value];
};

// #endregion

// #region Teams By Ids

// getTeamsByIds ----------------------------------------------------------
const selectTeamsByIds = createSelector(
  [
    (state: RootState) => state.teams.byId.entries,
    (_: RootState, ids: Guid[]) => ids,
  ],
  (entries, ids) =>
    ids
      .map((id) => entries[id.value])
      .filter((x): x is TeamJSON => !!x)
      .map((json) => Team.fromJSON(json)),
);
export const getTeamsByIds = (ids: Guid[]) => (state: RootState) => selectTeamsByIds(state, ids);
// ----------------------------------------------------------------------------

// getLoadingTeamsByIds ---------------------------------------------------
const selectTeamsLoadingByIds = createSelector(
  [(state: RootState) => state.teams.byId.loading],
  (loadingMap: Record<string, boolean>) => {
    return Object.keys(loadingMap)
      .map((idValue) => Guid.fromJSON(idValue))
      .filter((id) => id instanceof Guid);
  },
);
export const getLoadingTeamsByIds = () => (state: RootState) => selectTeamsLoadingByIds(state);
// ----------------------------------------------------------------------------

// getErrorLoadingTeamsByIds ----------------------------------------------
const selectTeamErrorsByIds = createSelector(
  [(state: RootState) => state.teams.byId.error],
  (errorMap: Record<string, SerializedError | null>) => {
    return Object.keys(errorMap)
      .map((idValue) => Guid.fromJSON(idValue))
      .filter((id) => id instanceof Guid);
  },
);
export const getErrorLoadingTeamsByIds = () => (state: RootState) => selectTeamErrorsByIds(state);
// ----------------------------------------------------------------------------

// #endregion

// #region Team Vendors

// getTeamVendors ----------------------------------------------------------
const selectTeamVendors = createSelector([(state: RootState) => state.teams.vendors.entries], (entries) =>
  !entries ? undefined : entries.map((json) => Team.fromJSON(json)).filter((c): c is Team => !!c),
);
export const getTeamVendors = () => (state: RootState) => selectTeamVendors(state);
// ----------------------------------------------------------------------------

export const getTeamVendorsIsLoading = () => (state: RootState) => state.teams.vendors.loading;

export const getTeamVendorsError = () => (state: RootState) => state.teams.vendors.error;

// #endregion

// #region Team Avatar

export const getTeamAvatar = (teamId: Guid | null) => (state: RootState) =>
  teamId ? state.teams.avatars.entries[teamId.value] : undefined;

export const getIsLoadingTeamAvatar = (teamId: Guid | null) => (state: RootState) =>
  teamId ? state.teams.avatars.loading[teamId.value] : false;

export const getErrorLoadingTeamAvatar = (teamId: Guid | null) => (state: RootState) =>
  teamId ? state.teams.avatars.error[teamId.value] : undefined;

// #endregion

// #region Team Invitation by Id

// getTeamInvitationById ------------------------------------------------------
const selectTeamInvitationById = createSelector(
  [
    (state: RootState) => state.teams.invitations.byId.entries,
    (_: RootState, id: Guid) => id,
  ],
  (entries, id) => {
    const invitationJson = entries[id.value];
    if (!invitationJson) return undefined;
    return TeamInvitation.fromJSON(invitationJson);
  },
);
export const getTeamInvitationById = (id: Guid) => (state: RootState) => selectTeamInvitationById(state, id);
// ----------------------------------------------------------------------------

export const getIsLoadingTeamInvitationById = (id: Guid) => (state: RootState) =>
  state.teams.invitations.byId.loading[id.value];

export const getErrorLoadingTeamInvitationById = (id: Guid) => (state: RootState) =>
  state.teams.invitations.byId.error[id.value];

// #endregion

// #region Team Invitation by TeamId

// getTeamInvitationsByTeamId -------------------------------------------------
const selectTeamInvitationsByTeamId = createSelector(
  [
    (state: RootState) => state.teams.invitations.byTeamId.entries,
    (_: RootState, teamId: Guid) => teamId,
  ],
  (entries, teamId) => {
    const invitationsJson = entries[teamId.value];
    if (!invitationsJson) return undefined;
    return invitationsJson.map((json) => TeamInvitation.fromJSON(json));
  },
);
export const getTeamInvitationsByTeamId = (teamId?: Guid) => (state: RootState) =>
  !teamId ? undefined : selectTeamInvitationsByTeamId(state, teamId);
// ----------------------------------------------------------------------------

export const getIsLoadingTeamInvitationsByTeamId = (teamId?: Guid) => (state: RootState) =>
  !teamId ? false : state.teams.invitations.byTeamId.loading[teamId.value];

export const getErrorLoadingTeamInvitationsByTeamId = (teamId?: Guid) => (state: RootState) =>
  !teamId ? undefined : state.teams.invitations.byTeamId.error[teamId.value];
// #endregion

export const { addMarketplaceTeam, addMarketplaceTeamJSON, removeMarketplaceTeam } = marketplaceTeamsSlice.actions;
